import React, { useState, useEffect, useRef } from "react";
import "./VerifyAccountStat.css";
import EyeIconClose from "../../../../assets/images/eye-block.svg";
import EyeIconOpen from "../../../../assets/images/eye.svg";
import checkIcon from "../../../../assets/images/check.svg";
import BinanceLogo from "../../../../assets/images/binance.svg";
import BybitLogo from "../../../../assets/images/bybit.svg";
import StarIcon from "../../../../assets/images/onboardingImages/Star.svg";
import useApi from "../../../../helpers/apiHelper/requestHelper";
import { showBalance } from "../../../../store/globalstore";
import { useAtom } from "jotai";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useDashboardMarket, getUserTradePermissionType } from "../../../sharedPages/CommonComponents/DashboardMarketSelect/DashboardMarketContext";

const PERIOD_OPTIONS = [
  { key: "week", label: "Last 7 d" },
  { key: "month", label: "Last 30 d" },
];

const STATS_PERIOD_OPTIONS = [
  { key: "7d", label: "Last 7 d", duration: "week" },
  { key: "month", label: "Last 30 d", duration: "month" },
];

const toFiniteNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const toNullableNumber = (value) => {
  if (value == null || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const firstNumber = (...values) => {
  for (const value of values) {
    const num = toNullableNumber(value);
    if (num != null) return num;
  }
  return null;
};

const getTradeSide = (item) => {
  const side = String(item?.side || "").toLowerCase();
  if (side === "buy" || side === "long") return "Long";
  if (side === "sell" || side === "short") return "Short";
  if (Number(item?.positionAmt) > 0) return "Long";
  if (Number(item?.positionAmt) < 0) return "Short";
  return item?.side || "—";
};

/** Same spot PnL derivation used by live trade cards. */
const resolveSpotPnl = (item) => {
  const isShort = getTradeSide(item) === "Short";
  const entry = firstNumber(
    item?.entryPrice,
    item?.avgPrice,
    item?.averagePrice,
    item?.costPrice,
    item?.purchasePrice,
    item?.avgEntryPrice
  );
  const mark = firstNumber(
    item?.markPrice,
    item?.currentPrice,
    item?.mark_price,
    item?.lastPrice,
    item?.price,
    item?.marketPrice
  );
  const size = firstNumber(
    item?.positionAmt,
    item?.qty,
    item?.quantity,
    item?.size,
    item?.executedQty
  );
  const absSize = size != null ? Math.abs(size) : null;

  let unrealized = firstNumber(
    item?.unRealizedProfit,
    item?.unrealizedProfit,
    item?.unrealizedPnl,
    item?.pnl,
    item?.unrealized,
    item?.uPnl
  );
  if (
    unrealized == null &&
    entry != null &&
    entry > 0 &&
    mark != null &&
    absSize
  ) {
    unrealized = isShort
      ? (entry - mark) * absSize
      : (mark - entry) * absSize;
  }

  let pnlPercentage = firstNumber(
    item?.pnlPercentage,
    item?.roe,
    item?.percentage,
    item?.unrealizedPnlPercentage,
    item?.pnlPercent
  );
  if (pnlPercentage == null && unrealized != null && entry != null && absSize) {
    const costBasis = Math.abs(entry * absSize);
    if (costBasis > 0) pnlPercentage = (unrealized / costBasis) * 100;
  }

  return pnlPercentage ?? unrealized ?? 0;
};

/** Win rate = profitable spot live trades / total spot live trades. */
const computeSpotWinRateFromLiveTrades = (trades = []) => {
  const total = trades.length;
  if (!total) {
    return { winRate: 0, winningTrades: 0, totalTrades: 0 };
  }
  const winningTrades = trades.reduce((count, item) => {
    return resolveSpotPnl(item) > 0 ? count + 1 : count;
  }, 0);
  return {
    winRate: (winningTrades / total) * 100,
    winningTrades,
    totalTrades: total,
  };
};

const formatMoney = (val, fractionDigits = 2) => {
  const num = Number(val);
  if (!Number.isFinite(num)) return "0";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits,
  });
};

const formatStatValue = (val, { prefix = "", suffix = "" } = {}) => {
  const num = Number(val);
  if (!Number.isFinite(num)) return `${prefix}0${suffix}`;
  return `${prefix}${formatMoney(num)}${suffix}`;
};

const getTone = (val) => {
  const num = Number(val);
  if (!Number.isFinite(num) || num === 0) return "positive";
  return num > 0 ? "positive" : "negative";
};

const VerifyAccountStat = ({ user, showRanking }) => {
  const api = useApi();
  const navigate = useNavigate();
  const { selectedMarket } = useDashboardMarket();
  const selectedCommunity = useSelector(
    (state) => state?.multiCommunity?.currentCommunity
  );

  const [connectedExchange, setConnectedExchange] = useState("Binance");
  const [yourTradingAccount, setYourTradingAccount] = useState(0);
  const [futuresBalance, setFuturesBalance] = useState(0);
  const [spotBalance, setSpotBalance] = useState(0);
  const [stats, setStats] = useState({});
  const [showBalanceFromStore, setShowBalanceInStore] = useAtom(showBalance);
  const [balanceSinceLastWeek, setBalanceSinceLastWeek] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState("week");
  const [isPeriodOpen, setIsPeriodOpen] = useState(false);
  const [selectedStatsPeriod, setSelectedStatsPeriod] = useState("7d");
  const [isStatsPeriodOpen, setIsStatsPeriodOpen] = useState(false);
  const [myRanking, setMyRanking] = useState(null);
  const [totalCommunities, setTotalCommunities] = useState(0);
  const [spotLiveTradeStats, setSpotLiveTradeStats] = useState({
    winRate: 0,
    winningTrades: 0,
    totalTrades: 0,
  });
  const periodDropdownRef = useRef(null);
  const statsPeriodDropdownRef = useRef(null);

  const viewOnBlockchainParams = (
    user?.leaderCommunity?.name ? user?.leaderCommunity.name : user?.tradeName
  )
    ?.split(" ")
    ?.join("_");
  const isVerifiedMyTrade = user?.verifiedmyTrade?.subscription?.active;
  const isConnected = Boolean(connectedExchange);
  const exchangeLogo =
    connectedExchange === "Bybit"
      ? BybitLogo
      : connectedExchange === "Binance"
      ? BinanceLogo
      : null;
  const isLeader = user?.role === "leader";
  const showLeaderRanking =
    showRanking &&
    isLeader &&
    (user?.leaderCommunity || (!user?.tradeName && !user?.leaderCommunity));

  const fetchStats = async () => {
    if (!user?._id) return;
    const duration =
      STATS_PERIOD_OPTIONS.find((option) => option.key === selectedStatsPeriod)
        ?.duration || "week";
    const response = await api(
      "get",
      `multiCommunity/getCurrentStats/${user._id}?duration=${duration}`
    );
    if (response?.status && response.data) {
      setStats((prevStats) => {
        const newStats = { ...response.data };
        // Duration-scoped responses can omit wallet fields — keep the last
        // known spot/futures values so Spot doesn't drop to $0.
        const balanceKeys = [
          "binanceBalance",
          "bybitBalance",
          "binanceSpotBalance",
          "bybitSpotBalance",
          "binanceFuturesBalance",
          "binanceFutureBalance",
          "binanceCoinFutureBalance",
          "bybitFuturesBalance",
          "bybitFutureBalance",
          "bybitCoinFutureBalance",
          "spotBalance",
          "futuresBalance",
          "coinFutureBalance",
          "balanceSinceLastWeek",
        ];
        balanceKeys.forEach((key) => {
          if (
            (newStats[key] === undefined || newStats[key] === null) &&
            prevStats?.[key] !== undefined &&
            prevStats?.[key] !== null
          ) {
            newStats[key] = prevStats[key];
          }
        });
        if (
          (newStats?.monthlyGrowth === undefined ||
            newStats?.monthlyGrowth === null) &&
          prevStats?.monthlyGrowth !== undefined &&
          prevStats?.monthlyGrowth !== null
        ) {
          newStats.monthlyGrowth = prevStats.monthlyGrowth;
        }
        return newStats;
      });
    }
  };

  const updateUserBalance = async (exchange) => {
    if (!exchange || !user?._id) return;
    // GET helper ignores the 3rd-arg payload — put params in the URL.
    await api(
      "get",
      `stats/updateUserBalance?exchange=${encodeURIComponent(exchange)}&id=${user._id}`
    );
  };

  useEffect(() => {
    let exchange = "";
    if (user?.binanceCredentials?.isConnected) {
      exchange = "Binance";
    } else if (user?.bybitCredentials?.isConnected) {
      exchange = "Bybit";
    }
    setConnectedExchange(exchange);
  }, [user]);

  useEffect(() => {
    if (!user?._id) return;
    let cancelled = false;

    const syncAndFetch = async () => {
      let exchange = "";
      if (user?.binanceCredentials?.isConnected) {
        exchange = "Binance";
      } else if (user?.bybitCredentials?.isConnected) {
        exchange = "Bybit";
      }
      if (exchange) {
        await updateUserBalance(exchange);
      }
      if (!cancelled) {
        await fetchStats();
      }
    };

    syncAndFetch();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id, selectedStatsPeriod]);

  useEffect(() => {
    const isConnectedNow =
      user?.binanceCredentials?.isConnected ||
      user?.bybitCredentials?.isConnected;

    if (!isConnectedNow) {
      setFuturesBalance(0);
      setSpotBalance(0);
      setYourTradingAccount(0);
      setBalanceSinceLastWeek(toFiniteNumber(stats?.balanceSinceLastWeek));
      return;
    }

    const isBinance = Boolean(user?.binanceCredentials?.isConnected);
    const isBybit = Boolean(user?.bybitCredentials?.isConnected);
    const exchangeRaw = isBinance
      ? stats?.binanceBalance
      : isBybit
      ? stats?.bybitBalance
      : null;
    const exchangeObj =
      exchangeRaw &&
      typeof exchangeRaw === "object" &&
      !Array.isArray(exchangeRaw)
        ? exchangeRaw
        : null;

    let spot = null;
    let futures = null;

    // Prefer dedicated spot fields (source of truth). Use pick that skips
    // null/undefined but keeps a real 0.
    const pickNumber = (...values) => {
      for (const value of values) {
        if (value == null || value === "") continue;
        const num = Number(value);
        if (Number.isFinite(num)) return num;
      }
      return null;
    };

    if (isBinance) {
      spot = pickNumber(
        stats?.binanceSpotBalance,
        exchangeObj?.spotBalance,
        exchangeObj?.balance,
        stats?.spotBalance
      );
      futures = pickNumber(
        exchangeObj?.coinFutureBalance,
        exchangeObj?.futuresBalance,
        exchangeObj?.futureBalance,
        stats?.binanceFuturesBalance,
        stats?.binanceFutureBalance,
        stats?.binanceCoinFutureBalance,
        typeof exchangeRaw === "number" || typeof exchangeRaw === "string"
          ? exchangeRaw
          : null
      );
    } else if (isBybit) {
      spot = pickNumber(
        stats?.bybitSpotBalance,
        exchangeObj?.spotBalance,
        exchangeObj?.balance,
        stats?.spotBalance
      );
      futures = pickNumber(
        exchangeObj?.coinFutureBalance,
        exchangeObj?.futuresBalance,
        exchangeObj?.futureBalance,
        stats?.bybitFuturesBalance,
        stats?.bybitFutureBalance,
        stats?.bybitCoinFutureBalance,
        typeof exchangeRaw === "number" || typeof exchangeRaw === "string"
          ? exchangeRaw
          : null
      );
    }

    const spotValue = spot ?? 0;
    const futuresValue = futures ?? 0;
    const permissionType = getUserTradePermissionType(user);
    const tradingAccountTotal =
      permissionType === "spot"
        ? spotValue
        : permissionType === "futures"
          ? futuresValue
          : futuresValue + spotValue;

    setFuturesBalance(futuresValue);
    setSpotBalance(spotValue);
    setYourTradingAccount(tradingAccountTotal);
    setBalanceSinceLastWeek(toFiniteNumber(stats?.balanceSinceLastWeek));
  }, [stats, user]);

  useEffect(() => {
    if (!showLeaderRanking) return;

    const getRanking = async () => {
      try {
        const response = await api("post", `/community/updateCommunityRankings`, {
          communityId: selectedCommunity?._id,
        });

        if (response && response.status) {
          setTotalCommunities(response?.data?.totalCommunities);
          setMyRanking(response?.data?.communityRanking);
        } else {
          const responsePro = await api(
            "get",
            "multiCommunity/allCommunityCount"
          );
          if (responsePro && responsePro.status) {
            if (
              user.role === "leader" &&
              !user.tradeName &&
              !user?.leaderCommunity
            ) {
              setTotalCommunities(responsePro?.data?.count);
              setMyRanking(responsePro?.data?.count);
            }
          }
        }
      } catch (err) {
        console.log(err);
      }
    };

    getRanking();
  }, [showLeaderRanking, selectedCommunity?._id, user]);

  useEffect(() => {
    let cancelled = false;

    const fetchSpotLiveTrades = async () => {
      if (selectedMarket !== "spot" || !user?._id) {
        setSpotLiveTradeStats({
          winRate: 0,
          winningTrades: 0,
          totalTrades: 0,
        });
        return;
      }

      try {
        const response = await api(
          "post",
          "livetrade/getLiveTrade?marketType=spot"
        );
        const openPositions = response?.data?.[0]?.openPositions || [];
        if (!cancelled) {
          setSpotLiveTradeStats(
            computeSpotWinRateFromLiveTrades(openPositions)
          );
        }
      } catch (err) {
        if (!cancelled) {
          setSpotLiveTradeStats({
            winRate: 0,
            winningTrades: 0,
            totalTrades: 0,
          });
        }
      }
    };

    fetchSpotLiveTrades();
    return () => {
      cancelled = true;
    };
  }, [selectedMarket, user?._id]);

  useEffect(() => {
    if (!isPeriodOpen && !isStatsPeriodOpen) return;

    const handleClickOutside = (event) => {
      if (
        periodDropdownRef.current &&
        !periodDropdownRef.current.contains(event.target)
      ) {
        setIsPeriodOpen(false);
      }
      if (
        statsPeriodDropdownRef.current &&
        !statsPeriodDropdownRef.current.contains(event.target)
      ) {
        setIsStatsPeriodOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isPeriodOpen, isStatsPeriodOpen]);

  const isWeeklyStats = selectedStatsPeriod === "7d";
  const isSpotMarket = selectedMarket === "spot";
  const permissionType = getUserTradePermissionType(user);
  const showFuturesBalance =
    permissionType === "futures" || permissionType === "both";
  const showSpotBalance =
    permissionType === "spot" || permissionType === "both";
  const isSingleMarketPermission =
    permissionType === "spot" || permissionType === "futures";
  const futuresAvgTradeRoi = toFiniteNumber(
    stats?.futures?.avgTradeRoi ??
      (isWeeklyStats
        ? stats?.avgTradeRoiWeek ?? stats?.weeklyAvgTradeRoi ?? stats?.avgTradeRoi ?? stats?.roi
        : stats?.avgTradeRoi ?? stats?.roi)
  );
  const spotAvgTradeRoi = toFiniteNumber(stats?.spot?.avgTradeRoi);
  const avgTradeRoi = isSpotMarket ? spotAvgTradeRoi : futuresAvgTradeRoi;
  const futuresWinRate = toFiniteNumber(
    isWeeklyStats
      ? stats?.winRateWeek ?? stats?.weeklyWinRate ?? stats?.winRate
      : stats?.winRate
  );
  const winRate = isSpotMarket
    ? toFiniteNumber(spotLiveTradeStats.winRate)
    : futuresWinRate;
  const spotTotalTrades = toFiniteNumber(spotLiveTradeStats.totalTrades);
  const spotWinningTrades = toFiniteNumber(spotLiveTradeStats.winningTrades);
  const futuresAum = toFiniteNumber(
    stats?.futures?.aum ?? stats?.AUM ?? stats?.aum ?? stats?.groupTradingVolume
  );
  const spotAum = toFiniteNumber(stats?.spot?.aum);
  const aum = isSpotMarket ? spotAum : futuresAum;
  const futuresCommunityGains = toFiniteNumber(
    stats?.futures?.communityGain ??
      (isWeeklyStats
        ? stats?.communityGainsWeek ??
            stats?.weeklyCommunityGains ??
            stats?.thisWeekPnlPercentage ??
            stats?.pnl
        : stats?.communityGains ?? stats?.communityGain ?? stats?.pnl)
  );
  const spotCommunityGain = toFiniteNumber(stats?.spot?.communityGain);
  const communityGains = isSpotMarket
    ? spotCommunityGain
    : futuresCommunityGains;
  const totalSubscribers =
    user?.leaderCommunity?.participants?.length ||
    toFiniteNumber(stats?.totalSubscribers) ||
    0;
  const unsubscribed = toFiniteNumber(
    isWeeklyStats
      ? stats?.unsubscribedWeek ??
          stats?.weeklyUnsubscribed ??
          stats?.unsubscribed ??
          stats?.unsubscribedCount
      : stats?.unsubscribed ?? stats?.unsubscribedCount
  );
  const newSubscribers = toFiniteNumber(
    isWeeklyStats
      ? stats?.newSubscribersWeek ??
          stats?.weeklyNewSubscribers ??
          stats?.userGrowthInWeekNumber ??
          stats?.newSubscribers ??
          stats?.newSubscriberCount
      : stats?.newSubscribers ?? stats?.newSubscriberCount
  );
  const growth = toFiniteNumber(
    isWeeklyStats
      ? stats?.weeklyUsersGrowth ??
          stats?.usersGrowthInWeek ??
          stats?.growthSinceLastWeek
      : stats?.usersGrowthInMonth ??
          stats?.growthSinceLastMonth ??
          stats?.monthlyGrowth?.growth ??
          stats?.monthlyGrowth
  );

  const weekChange = balanceSinceLastWeek;
  const monthChange = toFiniteNumber(
    stats?.growthSinceLastMonth ?? stats?.monthlyGrowth?.growth ?? growth
  );
  const periodChange = selectedPeriod === "month" ? monthChange : weekChange;
  const periodChangePositive = periodChange >= 0;
  const selectedPeriodLabel =
    PERIOD_OPTIONS.find((option) => option.key === selectedPeriod)?.label ||
    "Last 7 days";
  const selectedStatsPeriodLabel =
    STATS_PERIOD_OPTIONS.find((option) => option.key === selectedStatsPeriod)
      ?.label || "Last 7 d";

  const rankingValue = `${
    myRanking || selectedCommunity?.myRanking || totalCommunities || 0
  }/${Number(totalCommunities || 0).toLocaleString()}`;

  const numberSizeClass = (val) => {
    const len = String(val || "").replace(/[^0-9]/g, "").length;
    if (len >= 6) return " longNumber extraLongNumber";
    if (len >= 5) return " longNumber";
    return "";
  };

  const tradingMetrics = [
    {
      label: "Avg. Trade ROI",
      value: formatStatValue(avgTradeRoi, { suffix: "%" }),
      tone: getTone(avgTradeRoi),
    },
    {
      label: "AUM",
      value: formatStatValue(aum),
      tone: getTone(aum),
    },
    {
      label: "Community Gains",
      value: formatStatValue(communityGains, { prefix: "$" }),
      tone: getTone(communityGains),
    },
    {
      label: isSpotMarket ? "Total Trades" : "Win Rate",
      value: isSpotMarket
        ? formatStatValue(spotTotalTrades)
        : formatStatValue(winRate, { suffix: "%" }),
      tone: isSpotMarket ? "positive" : getTone(winRate),
    },
  ];

  // Backend doesn't return these yet — stubbed at 0 until it does.
  const pnlPercent = toFiniteNumber(stats?.pnl);
  const maxDrawdown = toFiniteNumber(stats?.maxDrawdown);
  const profitFactor = toFiniteNumber(stats?.profitFactor);
  const avgMonthlyReturn = toFiniteNumber(stats?.avgMonthlyReturn);
  const winningTrades = isSpotMarket
    ? spotWinningTrades
    : toFiniteNumber(stats?.winningTrades);

  const isVerifyTradingPurpose = Boolean(user?.tradeName) && !user?.leaderCommunity;

  const traderVerifyMetrics = [
    {
      label: "PnL (%)",
      value: formatStatValue(pnlPercent, { suffix: "%" }),
      tone: getTone(pnlPercent),
    },
    {
      label: "Avg. Trade ROI",
      value: formatStatValue(avgTradeRoi, { suffix: "%" }),
      tone: getTone(avgTradeRoi),
    },
    {
      label: "Max Drawdown (%)",
      value: formatStatValue(maxDrawdown, { suffix: "%" }),
      tone: getTone(maxDrawdown),
    },
    {
      label: isSpotMarket ? "Total Trades" : "Win Rate (%)",
      value: isSpotMarket
        ? formatStatValue(spotTotalTrades)
        : formatStatValue(winRate, { suffix: "%" }),
      tone: isSpotMarket ? "positive" : getTone(winRate),
    },
    {
      label: "Profit Factor",
      value: formatStatValue(profitFactor),
      tone: getTone(profitFactor),
    },
    {
      label: "Average Monthly Return",
      value: formatStatValue(avgMonthlyReturn, { suffix: "%" }),
      tone: getTone(avgMonthlyReturn),
    },
    {
      label: "Avg. Trade ROI",
      value: formatStatValue(avgTradeRoi, { suffix: "%" }),
      tone: getTone(avgTradeRoi),
    },
    {
      label: "Winning Trades",
      value: formatStatValue(winningTrades),
      tone: getTone(winningTrades),
    },
  ];

  const businessMetrics = [
    {
      label: "Total Subscribers",
      value: formatStatValue(totalSubscribers),
      tone: getTone(totalSubscribers),
    },
    {
      label: "Unsubscribed",
      value: formatStatValue(unsubscribed),
      tone: unsubscribed > 0 ? "negative" : "positive",
    },
    {
      label: "New Subscribers",
      value: formatStatValue(newSubscribers),
      tone: getTone(newSubscribers),
    },
    {
      label: "Growth",
      value: formatStatValue(growth, { suffix: "%" }),
      tone: getTone(growth),
    },
  ];

  const handleBlockchainClick = () => {
    if (isVerifiedMyTrade) {
      navigate(`/verifiedTrades/${viewOnBlockchainParams}`);
      return;
    }
    navigate("/leader/verifymyTrade/tradeinfo");
  };

  const renderMetricGrid = (items, modifierClass = "") => (
    <div className={`verifyCopyStatsCard__grid${modifierClass ? ` ${modifierClass}` : ""}`}>
      {items.map((item) => (
        <div
          key={item.label}
          className={`verifyCopyStatsCard__item verifyCopyStatsCard__item--${item.tone}`}
        >
          <p className="verifyCopyStatsCard__label">{item.label}</p>
          <p className="verifyCopyStatsCard__value">{item.value}</p>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <div className="verifyAccountCard">
        <div className="verifyAccountCard__heading">
          <p className="verifyAccountCard__title">Your trading account</p>
          <button
            type="button"
            className="verifyAccountCard__eyeBtn"
            onClick={() => setShowBalanceInStore(!showBalanceFromStore)}
          >
            {showBalanceFromStore ? (
              <img src={EyeIconClose} alt="close Icon" />
            ) : (
              <img src={EyeIconOpen} alt="open Icon" />
            )}
          </button>
        </div>

        <div className="verifyAccountCard__exchangeRow">
          {isConnected ? (
            <>
              <div className="verifyAccountCard__exchangeIdentity">
                {exchangeLogo && (
                  <img
                    src={exchangeLogo}
                    alt={connectedExchange}
                    className="verifyAccountCard__exchangeLogo"
                  />
                )}
              </div>
              {isLeader && (
                <button
                  type="button"
                  className={`verifyAccountCard__verifiedBadge ${
                    isVerifiedMyTrade ? "is-verified" : ""
                  }`}
                  onClick={handleBlockchainClick}
                >
                  {isVerifiedMyTrade && (
                    <img src={checkIcon} alt="" aria-hidden="true" />
                  )}
                  <span>
                    {isVerifiedMyTrade
                      ? "Blockchain Verified"
                      : "Verify your trading"}
                  </span>
                </button>
              )}
            </>
          ) : (
            <span className="verifyAccountCard__disconnected">
              You are disconnected
            </span>
          )}
        </div>

        <p
          className={`verifyAccountCard__numbers${numberSizeClass(
            showBalanceFromStore ? yourTradingAccount : "****"
          )}`}
        >
          {showBalanceFromStore ? (
            <>
              <span>$</span>{" "}
              {formatMoney(yourTradingAccount)}
            </>
          ) : (
            <span className="verifyAccountCard__stars">****</span>
          )}
        </p>

        <div
          className={`verifyAccountCard__footer${
            isSingleMarketPermission
              ? " verifyAccountCard__footer--singleMarket"
              : ""
          }`}
        >
          <div className="verifyAccountCard__breakdown">
            {showFuturesBalance && (
              <p>
                Futures:{" "}
                <span>
                  $
                  {showBalanceFromStore
                    ? formatMoney(futuresBalance)
                    : "****"}
                </span>
              </p>
            )}
            {showSpotBalance && (
              <p>
                Spot:{" "}
                <span>
                  $
                  {showBalanceFromStore ? formatMoney(spotBalance) : "****"}
                </span>
              </p>
            )}
          </div>

          <div className="verifyAccountCard__periodWrap" ref={periodDropdownRef}>
            <button
              type="button"
              className={`verifyAccountCard__periodBtn ${
                periodChangePositive ? "positive" : "negative"
              }`}
              aria-expanded={isPeriodOpen}
              aria-haspopup="listbox"
              onClick={() => setIsPeriodOpen((open) => !open)}
            >
              <span className="verifyAccountCard__periodIcon" aria-hidden="true">
                <span />
                <span />
              </span>
              <span className="verifyAccountCard__periodLabel">
                {selectedPeriodLabel}:{" "}
                <span className="verifyAccountCard__periodValue">
                  {periodChangePositive ? "+" : ""}
                  {formatMoney(periodChange)}%
                </span>
              </span>
            </button>

            {isPeriodOpen && (
              <ul className="verifyAccountCard__periodMenu" role="listbox">
                {PERIOD_OPTIONS.map((option) => (
                  <li key={option.key}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={selectedPeriod === option.key}
                      className={
                        selectedPeriod === option.key ? "is-active" : ""
                      }
                      onClick={() => {
                        setSelectedPeriod(option.key);
                        setIsPeriodOpen(false);
                      }}
                    >
                      {option.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="verifyCopyStatsCard">
        <div className="verifyCopyStatsCard__header">
          <h3 className="verifyCopyStatsCard__title">
            Copy Trading Statistics{" "}
            <span
              className={`verifyCopyStatsCard__periodSelect${
                isStatsPeriodOpen ? " is-open" : ""
              }`}
              ref={statsPeriodDropdownRef}
            >
              <button
                type="button"
                className="verifyCopyStatsCard__periodHint"
                aria-expanded={isStatsPeriodOpen}
                aria-haspopup="listbox"
                onClick={() => setIsStatsPeriodOpen((open) => !open)}
              >
                <span
                  className="verifyCopyStatsCard__periodIcon"
                  aria-hidden="true"
                >
                  <span />
                  <span />
                </span>
                ({selectedStatsPeriodLabel})
              </button>

              {isStatsPeriodOpen && (
                <ul
                  className="verifyCopyStatsCard__periodMenu"
                  role="listbox"
                >
                  {STATS_PERIOD_OPTIONS.map((option) => (
                    <li key={option.key}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={selectedStatsPeriod === option.key}
                        className={
                          selectedStatsPeriod === option.key ? "is-active" : ""
                        }
                        onClick={() => {
                          setSelectedStatsPeriod(option.key);
                          setIsStatsPeriodOpen(false);
                        }}
                      >
                        {option.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </span>
          </h3>
          {showLeaderRanking && (
            <div className="verifyCopyStatsCard__ranking">
              <img src={StarIcon} alt="" aria-hidden="true" />
              <span>Ranking {rankingValue}</span>
            </div>
          )}
        </div>

        <div
          className={`verifyCopyStatsCard__body${
            isVerifyTradingPurpose ? " verifyCopyStatsCard__body--soloTrading" : ""
          }`}
        >
          <section className="verifyCopyStatsCard__section">
            <h4 className="verifyCopyStatsCard__sectionTitle">
              {isSpotMarket ? "Spot Trading" : "Futures Trading"}
            </h4>
            {isVerifyTradingPurpose
              ? renderMetricGrid(
                  traderVerifyMetrics,
                  "verifyCopyStatsCard__grid--traderCols"
                )
              : renderMetricGrid(tradingMetrics)}
          </section>

          {!isVerifyTradingPurpose && (
            <>
              <div
                className="verifyCopyStatsCard__divider"
                aria-hidden="true"
              />

              <section className="verifyCopyStatsCard__section">
                <h4 className="verifyCopyStatsCard__sectionTitle">
                  Business
                </h4>
                {renderMetricGrid(businessMetrics)}
              </section>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default VerifyAccountStat;
